/**
 * MyTasksFilterBar
 * Filter row for the My Tasks page.
 * Contains a workflow type dropdown and a status dropdown.
 */

import { Filter } from 'lucide-react';
import styled from 'styled-components';
import { FilterBar } from '../../components/tasks';

// ============================================================================
// Types
// ============================================================================

export interface MyTasksFilterBarProps {
  workflowFilter: string;
  statusFilter: string;
  onWorkflowChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  workflowOptions: Array<{ value: string; label: string }>;
}

// ============================================================================
// Styled components
// ============================================================================

const FilterIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
`;

const Select = styled.select`
  height: 36px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: inherit;
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast},
    box-shadow ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.borders.dark};
  }
`;

// ============================================================================
// Static options
// ============================================================================

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'BLOCKED', label: 'Blocked' },
  { value: 'COMPLETED', label: 'Completed' },
];

// ============================================================================
// Component
// ============================================================================

export function MyTasksFilterBar({
  workflowFilter,
  statusFilter,
  onWorkflowChange,
  onStatusChange,
  workflowOptions,
}: MyTasksFilterBarProps) {
  const allWorkflowOptions = [
    { value: 'all', label: 'All Workflows' },
    ...workflowOptions,
  ];

  return (
    <FilterBar>
      <FilterIcon aria-hidden="true">
        <Filter size={16} />
      </FilterIcon>

      <Select
        value={workflowFilter}
        onChange={(e) => onWorkflowChange(e.target.value)}
        aria-label="Filter by workflow type"
      >
        {allWorkflowOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      <Select
        value={statusFilter}
        onChange={(e) => onStatusChange(e.target.value)}
        aria-label="Filter by task status"
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

    </FilterBar>
  );
}

export default MyTasksFilterBar;
